import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, X } from 'lucide-react';

/**
 * A notice above a page — regulatory warnings, advisories, setup prompts.
 * Styles: `.fd-notice*` in styles/components.css.
 *
 * Severity is carried three ways at once (tone colour, icon, and the words of
 * the title) so it never depends on colour alone.
 *
 * `details` is an optional explanation behind a "Details" toggle. Use it for
 * the WHY, never for the obligation itself: a deadline, a threshold or a
 * required action belongs in `children`, where it is always on screen. The
 * expanded state is per-render only — opening it persists nothing.
 *
 * Dismissal is the caller's: pass `onDismiss` and the caller decides what
 * dismissing means (most notices here record it per org in localStorage).
 * Without it there is no close button — a danger notice should not have one.
 */
export interface NoticeProps {
    tone: 'danger' | 'warning' | 'info';
    icon: React.ElementType;
    title: ReactNode;
    children: ReactNode;
    /** Primary action(s), e.g. a link to GOV.UK. */
    actions?: ReactNode;
    details?: ReactNode;
    onDismiss?: () => void;
    dismissLabel?: string;
}

export const Notice = ({
    tone,
    icon: Icon,
    title,
    children,
    actions,
    details,
    onDismiss,
    dismissLabel = 'Dismiss',
}: NoticeProps) => {
    const [expanded, setExpanded] = useState(false);
    const id = useId();
    const titleId = `${id}-title`;
    const detailId = `${id}-detail`;

    return (
        <section className={`fd-notice fd-notice-${tone}`} aria-labelledby={titleId}>
            <span className="fd-notice-icon" aria-hidden="true">
                <Icon strokeWidth={2.25} />
            </span>

            <div className="min-w-0">
                <h2 id={titleId} className="fd-notice-title">{title}</h2>
                <div className="fd-notice-text">{children}</div>
            </div>

            {(actions || details) && (
                <div className="fd-notice-actions">
                    {actions}
                    {details && (
                        <button
                            type="button"
                            className="fd-notice-toggle"
                            aria-expanded={expanded}
                            aria-controls={detailId}
                            onClick={() => setExpanded((v) => !v)}
                        >
                            {expanded ? 'Hide details' : 'Details'}
                            <ChevronDown aria-hidden="true" />
                        </button>
                    )}
                </div>
            )}

            {onDismiss && (
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label={dismissLabel}
                    title={dismissLabel}
                    className="fd-btn fd-btn-icon fd-notice-dismiss"
                >
                    <X />
                </button>
            )}

            {details && expanded && (
                <div id={detailId} className="fd-notice-detail">
                    {details}
                </div>
            )}
        </section>
    );
};
